import { Dialog, Stack } from "@tm/components"
import { useMemo } from "react"
import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ensureNumber } from "@tm/utils"
import { useGetFormValidationError } from "@tm/localization"
import { PartBasicInfo } from "../../../../../_shared/PartDialog/PartBasicInfo"
import { EditDialogHeader } from "../../../../../_shared/PartDialog/EditDialogHeader"
import { PartPriceFields } from "./PartPriceFields"
import { BasketPart, BasicBasketPartInfo } from "../../../../../../models"

type Props = {
    part: BasketPart
    hidePurchasePrice?: boolean
    showManufacturer: boolean
    showSupplierArticleNumbers: boolean
    showWholesalerArticleNumbers: boolean
    currencyCode: string
    disableHeaderButtons?: boolean
    onCloseButtonClick(): void
    onEditPart?(part: BasketPart, editBasketPartInfo: BasicBasketPartInfo): void
}

export function PartEditDialog(props: Props) {
    const getError = useGetFormValidationError()
    const {
        part,
        hidePurchasePrice,
        currencyCode,
        showManufacturer,
        showSupplierArticleNumbers,
        showWholesalerArticleNumbers,
        disableHeaderButtons,
        onEditPart,
        onCloseButtonClick,
    } = props
    const { calculatedPart } = part

    const defaultValues = useMemo(() => {
        return {
            regularPrice: calculatedPart?.regularPrice?.value ?? 0,
            surcharge: calculatedPart?.surcharge ?? 0,
            regularPriceWithSurcharge: calculatedPart?.regularPriceWithSurcharge?.value ?? 0,
            rebate: calculatedPart?.rebate ?? 0,
            offerPrice: calculatedPart?.offerPrice?.value ?? 0,
        }
    }, [calculatedPart])

    const schema = useMemo(() => {
        return z.object({
            regularPrice: ensureNumber(z.number({ required_error: getError("REQUIRED") }).gte(0)),
            surcharge: ensureNumber(z.number({ required_error: getError("REQUIRED") }).gte(0)),
            regularPriceWithSurcharge: z.number({ required_error: getError("REQUIRED") }),
            rebate: ensureNumber(z.number({ required_error: getError("REQUIRED") }).gte(0)),
            offerPrice: z.number({ required_error: getError("REQUIRED") }),
        })
    }, [])

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const { handleSubmit } = form

    function handleConfirmButtonClick(data: z.infer<typeof schema>) {
        onEditPart?.(part, data)
        onCloseButtonClick()
    }

    return (
        <Dialog open position="middle" fullWidth maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                    <Stack gap={2}>
                        <EditDialogHeader isSaving={disableHeaderButtons} onCloseButtonClick={onCloseButtonClick} />
                        <Stack gap={2}>
                            <PartBasicInfo
                                part={part}
                                hidePurchasePrice={hidePurchasePrice}
                                showManufacturer={showManufacturer}
                                showSupplierArticleNumbers={showSupplierArticleNumbers}
                                showWholesalerArticleNumbers={showWholesalerArticleNumbers}
                            />
                            <Stack direction="row" flex={1} gap={3} alignItems="center" alignSelf="flex-end">
                                <PartPriceFields currencyCode={currencyCode} />
                            </Stack>
                        </Stack>
                    </Stack>
                </form>
            </FormProvider>
        </Dialog>
    )
}
